
import React from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Trash2, Tag, RefreshCw } from "lucide-react";
import { EmailDraft } from "@/utils/investors/draftUtils";

interface EmailDraftsProps {
  drafts: EmailDraft[];
  selectedDraftId: string | null;
  onSelectDraft: (draft: EmailDraft) => void;
  onDeleteDraft: (draftId: string) => void;
  onLabelDraft: (draftId: string, label: string) => void;
  onRegenerateDraft: (draft: EmailDraft) => void;
  isLoading: boolean;
}

const EmailDrafts: React.FC<EmailDraftsProps> = ({
  drafts,
  selectedDraftId,
  onSelectDraft,
  onDeleteDraft,
  onLabelDraft,
  onRegenerateDraft,
  isLoading
}) => {
  if (drafts.length === 0) {
    return (
      <div className="text-center p-4 text-gray-500">
        No saved drafts found.
      </div>
    );
  }

  return (
    <ScrollArea className="h-80">
      <div className="space-y-2 p-1">
        {drafts.map((draft) => {
          const isSelected = draft.id === selectedDraftId;
          const draftDate = new Date(draft.createdAt || Date.now());
          
          return (
            <div 
              key={draft.id}
              className={`border rounded-md p-3 ${
                isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200"
              }`}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="font-medium truncate flex-1">
                  {draft.label || `Draft ${draftDate.toLocaleTimeString()}`}
                </div>
                <div className="flex space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRegenerateDraft(draft)}
                    disabled={isLoading}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteDraft(draft.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
              
              <div className="text-xs text-gray-500 mb-2">
                {draftDate.toLocaleDateString()} {draftDate.toLocaleTimeString()}
              </div>
              
              <div className="text-sm line-clamp-2 text-gray-700 mb-2">
                {draft.content.substring(0, 100)}...
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <Input
                    placeholder="Add label..."
                    defaultValue={draft.label || ""}
                    onBlur={(e) => onLabelDraft(draft.id, e.target.value)}
                    className="h-7 text-xs"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-2"
                  onClick={() => onSelectDraft(draft)}
                >
                  Use
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
};

export default EmailDrafts;
